import React, { useEffect, useState } from "react";
import { getOrganizations, deleteOrganization, createOrganization, updateOrganization } from "../../controllers/OrganizationController";
import { Button, Modal, Input, Table, Upload, message, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { convertToBase64 } from "../../utils/Utils";
import "./OrganizationCreation.css";

const OrganizationsTable = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [form, setForm] = useState({
    name: "",
    logo_base64: "",
    colour_theme: "",
  });

  const primaryColors = [
    { label: 'Rouge', value: 'red' },
    { label: 'Bleu', value: 'blue' },
    { label: 'Vert', value: 'green' },
    { label: 'Jaune', value: 'yellow' },
    { label: 'Mauve', value: 'purple' },
    { label: 'Orange', value: 'orange' },
    { label: 'Rose', value: 'pink' },
    { label: 'Brun', value: 'brown' },
    { label: 'Gris', value: 'gray' },
    { label: 'Noir', value: 'black' },
    { label: 'Blanc', value: 'white' }
  ];

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
    setLoading(false);
  };

  const handleEdit = (record) => {
    setEditingOrganization(record);
    setForm(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    await deleteOrganization(id);
    fetchOrganizations();
  };

  const handleCreate = () => {
    setEditingOrganization(null);
    setForm({ name: "", logo_base64: "", colour_theme: "" });
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    if (editingOrganization) {
      await updateOrganization(editingOrganization.id, form);
    } else {
      await createOrganization(form);
    }
    setIsModalVisible(false);
    fetchOrganizations();
  };

  const handleUpload = (file) => {
    convertToBase64(file).then((base64) => {
      setForm({ ...form, logo_base64: base64 });
    }).catch(() => {
      message.error("Erreur de conversion de l'image");
    });
    return false;
  };

  const columns = [
    { title: "Nom", dataIndex: "name", key: "name" },
    {
      title: "Logo",
      dataIndex: "logo_base64",
      key: "logo_base64",
      render: (text) => <img src={text} alt="Logo" width={50} />
    },
    {
      title: "Couleur",
      dataIndex: "colour_theme",
      key: "colour_theme",
      render: (color) => (
        <span
          style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: color, // Utilise la couleur du thème pour la pastille
            marginRight: 8,
            border: color === 'white' ? '1px solid #ddd' : 'none', // Ajoute une bordure pour le blanc
          }}
        ></span>
      )
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button onClick={() => handleEdit(record)}>Modifier</Button>
          <Button danger onClick={() => handleDelete(record.id)} style={{ marginLeft: 8 }}>
            Supprimer
          </Button>
        </>
      ),
    },
  ];

  return (
    <div>
      <Table
        dataSource={organizations}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ y: 500 }}
        style={{ color: 'blue' }}
        pagination={{
          pageSize: 10,  // Nombre d'éléments par page
          showSizeChanger: false,  // Permet à l'utilisateur de changer la taille de la page
          showQuickJumper: false,  // Permet de sauter à une page rapidement
        }}  // This enables vertical scrolling with a fixed height of 400px
      />

      <Modal
        title={editingOrganization ? "Modifier l'organisation" : "Créer une organisation"}
        visible={isModalVisible}
        onOk={handleSave}
        onCancel={() => setIsModalVisible(false)}
      >
        <Input
          placeholder="Nom"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          style={{ marginBottom: 8 }}
        />

        {form.logo_base64 && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <p style={{ marginBottom: 4 }}>Aperçu du logo:</p>
            <img
              src={form.logo_base64}
              alt="Logo Preview"
              style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
            />
          </div>
        )}

        <Upload
          showUploadList={false}
          beforeUpload={handleUpload}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />} style={{ marginBottom: 8 }}>
            Téléverser un logo
          </Button>
        </Upload>

        <Select
          placeholder="Couleur"
          value={form.colour_theme}
          onChange={(value) => setForm({ ...form, colour_theme: value })}
          style={{ width: '100%', marginBottom: 8 }}
        >
          {primaryColors.map((color) => (
            <Select.Option key={color.value} value={color.value}>
              <span
                style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: color.value,
                  marginRight: 8,
                  border: color.value === 'white' ? '1px solid #ddd' : 'none'
                }}
              ></span>
              {color.label}
            </Select.Option>
          ))}
        </Select>

      </Modal>

      <Button type="primary" onClick={handleCreate} style={{ marginBottom: 16 }}>
        Ajouter une organisation
      </Button>
    </div>
  );
};

export default OrganizationsTable;
